import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui-common/card';
import { Button } from './ui-common/button';
import { Input } from './ui-common/input';
import { Badge } from './ui-common/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui-common/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui-common/tooltip';
import { 
  Search, 
  Clock, 
  Video, 
  BookOpen, 
  Users, 
  CheckCircle,
  Circle,
  AlertCircle,
  BookMarked,
  Sparkles,
  MessageSquare,
  ChevronDown,
  Calendar,
  ExternalLink,
  PanelLeftClose,
  ArrowUpDown,
  Captions,
  Copy,
  X
} from 'lucide-react';
import MarkdownRenderer from './MarkdownRenderer';
import { getTextDirection, isHebrew } from '../lib/utils';
import axiosInstance from './axiosInstance';

const LecturesMaterialsPanel = ({ course, user, onSendTextToChat, onCollapse }) => {
  const [activeTab, setActiveTab] = useState('lectures');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('most_recent');
  const [lectures, setLectures] = useState([]);
  const [recitations, setRecitations] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isTimeoutError, setIsTimeoutError] = useState(false);
  const [selectedLecture, setSelectedLecture] = useState(null);
  const [selectedText, setSelectedText] = useState('');
  const [selectionPosition, setSelectionPosition] = useState({ x: 0, y: 0 });
  const [showSelectionTooltip, setShowSelectionTooltip] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [summaryModalLecture, setSummaryModalLecture] = useState(null);
  const hasSetInitialTab = useRef(false);
  const previousLoadingRef = useRef(false); // Track previous loading state
  const summaryModalRef = useRef(null);

  useEffect(() => {
    if (course) {
      fetchLecturesData();
      // Reset the flag when course changes
      hasSetInitialTab.current = false;
      previousLoadingRef.current = false;
    }
  }, [course]);

  // Set default tab to 'lectures' when lectures data is first loaded (if lectures tab exists)
  // Only do this once on initial load, then allow user to switch tabs freely
  useEffect(() => {
    // Only act when loading transitions from true to false (data just finished loading)
    const wasLoading = previousLoadingRef.current;
    const isLoading = loading;
    previousLoadingRef.current = loading;
    
    // Only proceed if we just finished loading (transition from true to false)
    if (wasLoading && !isLoading) {
      const availableTabIds = [];
      if (lectures.length > 0) availableTabIds.push('lectures');
      if (recitations.length > 0) availableTabIds.push('recitations');
      availableTabIds.push('materials');
      
      // Set initial default tab only once when data first loads
      if (!hasSetInitialTab.current && availableTabIds.length > 0) {
        const preferredTab = availableTabIds.includes('lectures') ? 'lectures' : availableTabIds[0];
        setActiveTab(preferredTab);
        hasSetInitialTab.current = true;
      }
    }
    
    // If current tab becomes unavailable (e.g., tab was removed), switch to valid tab
    // But only check this after initial tab has been set
    if (hasSetInitialTab.current && !loading) {
      const availableTabIds = [];
      if (lectures.length > 0) availableTabIds.push('lectures');
      if (recitations.length > 0) availableTabIds.push('recitations');
      availableTabIds.push('materials');
      
      if (!availableTabIds.includes(activeTab)) {
        const preferredTab = availableTabIds.includes('lectures') ? 'lectures' : availableTabIds[0];
        setActiveTab(preferredTab);
      }
    }
  }, [lectures.length, recitations.length, loading, activeTab]);

  // Handle text selection and right-click menu
  useEffect(() => {
    const handleRightClick = (e) => {
      const selection = window.getSelection();
      const text = selection.toString().trim();
      
      // Only show menu if there's a selection and it's within the panel
      if (text && text.length > 0) {
        const panel = document.querySelector('.lectures-panel');
        if (panel && panel.contains(selection.anchorNode)) {
          e.preventDefault(); // Prevent default context menu
          
          setSelectedText(text);
          
          // Get selection position more accurately
          const range = selection.getRangeAt(0);
          const rect = range.getBoundingClientRect();
          const panelRect = panel.getBoundingClientRect();
          
          setSelectionPosition({
            x: e.clientX - panelRect.left,
            y: e.clientY - panelRect.top
          });
          setShowSelectionTooltip(true);
        }
      }
    };

    const handleClick = (e) => {
      // Hide the tooltip on any click (left or right)
      if (showSelectionTooltip) {
        // Check if clicking on the tooltip itself
        const tooltip = document.querySelector('.selection-tooltip');
        if (tooltip && tooltip.contains(e.target)) {
          return; // Don't hide if clicking on the tooltip
        }
        
        setShowSelectionTooltip(false);
        setSelectedText('');
        // Clear selection when hiding tooltip
        window.getSelection().removeAllRanges();
      }
    };

    const panel = document.querySelector('.lectures-panel');
    
    // Add right-click handler for context menu
    if (panel) {
      panel.addEventListener('contextmenu', handleRightClick);
    }
    
    // Add click handler to hide tooltip
    document.addEventListener('click', handleClick);
    
    return () => {
      if (panel) {
        panel.removeEventListener('contextmenu', handleRightClick);
      }
      document.removeEventListener('click', handleClick);
    };
  }, [showSelectionTooltip]);

  const handleSendToChat = () => {
    if (selectedText && onSendTextToChat) {
      console.log('Sending to chat:', selectedText); // Debug log
      onSendTextToChat(selectedText);
      setShowSelectionTooltip(false);
      setSelectedText('');
      // Clear selection after sending
      window.getSelection().removeAllRanges();
    }
  };

  const toggleSection = (lectureId, section) => {
    const key = `${lectureId}-${section}`;
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const openSummaryModal = (lecture) => {
    setSummaryModalLecture(lecture);
  };

  const closeSummaryModal = () => {
    setSummaryModalLecture(null);
  };

  const handleCopySummary = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const getWordCount = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => {
      // Check if this part matches the search term (case-insensitive)
      if (part.toLowerCase() === searchTerm.toLowerCase()) {
        return (
          <mark key={index} className="bg-yellow-200 dark:bg-yellow-600 text-yellow-900 dark:text-yellow-100 px-1 rounded">
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  const fetchLecturesData = async () => {
    if (!course) return;
    
    setLoading(true);
    setError(null);
    setIsTimeoutError(false);
    
    try {
      // Fetch lectures (type: "Lecture")
      const lecturesResponse = await axiosInstance.post('/api/student/summaries', {
        conversation_course: course.name,
        query: "lectures",
        type_filter: "Lecture"
      });
      
      // Check if it's a timeout error
      if (lecturesResponse.data.status === 'timeout') {
        setIsTimeoutError(true);
        setLectures([]);
        setRecitations([]);
        setMaterials([]);
        return;
      }
      
      if (lecturesResponse.data.status === 'success') {
        // Ensure data is an array, not an error dict
        const data = lecturesResponse.data.data;
        if (Array.isArray(data)) {
          setLectures(data);
        } else {
          // If it's an error dict or unexpected format, set empty array
          console.warn('Unexpected data format for lectures:', data);
          setLectures([]);
        }
      }

      // Fetch recitations (type: "Exercise")
      const recitationsResponse = await axiosInstance.post('/api/student/summaries', {
        conversation_course: course.name,
        query: "exercises",
        type_filter: "Exercise"
      });
      
      // Check if it's a timeout error
      if (recitationsResponse.data.status === 'timeout') {
        setIsTimeoutError(true);
        setLectures([]);
        setRecitations([]);
        setMaterials([]);
        return;
      }
      
      if (recitationsResponse.data.status === 'success') {
        // Ensure data is an array, not an error dict
        const data = recitationsResponse.data.data;
        if (Array.isArray(data)) {
          setRecitations(data);
        } else {
          // If it's an error dict or unexpected format, set empty array
          console.warn('Unexpected data format for recitations:', data);
          setRecitations([]);
        }
      }

      // TODO: Fetch materials when API endpoints are available
      setMaterials([]);
      
    } catch (err) {
      console.error('Error fetching lectures data:', err);
      setError('Failed to load lectures and materials');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '00:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('he-IL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return <Badge variant="default" className="bg-green-500 text-white hover:bg-green-600"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'in_progress':
        return <Badge variant="default" className="bg-yellow-500 text-white hover:bg-yellow-600"><AlertCircle className="w-3 h-3 mr-1" />In Progress</Badge>;
      default:
        return <Badge variant="outline" className="border-border text-muted-foreground"><Circle className="w-3 h-3 mr-1" />Not Started</Badge>;
    }
  };

  const filterItems = (items) => {
    if (!searchQuery) return items;
    return items.filter(item => 
      item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.summary?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.topics?.some(topic => topic.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  };

  const sortItems = (items) => {
    if (!items || items.length === 0) return items;
    
    return [...items].sort((a, b) => {
      const dateA = new Date(a.date || a.upload_date || 0);
      const dateB = new Date(b.date || b.upload_date || 0);
      
      if (sortOrder === 'most_recent') {
        return dateB - dateA; // Newest first
      } else {
        return dateA - dateB; // Oldest first
      }
    });
  };

  const LectureCard = ({ lecture, type = 'lecture' }) => {
    const lectureIsHebrew = isHebrew(lecture.name || '');
    const dir = getTextDirection(lecture.name || '');
    const textAlign = lectureIsHebrew ? 'right' : 'left';
    const lectureId = lecture.id || 'unknown';
    const topicsKey = `${lectureId}-topics`;
    const isTopicsExpanded = expandedSections[topicsKey];
    const wordCount = getWordCount(lecture.summary);

    // Handle topic click - open summary modal
    const handleTopicClick = (topicText, topicIndex, e) => {
      e.stopPropagation(); // Prevent toggling the topics section
      openSummaryModal(lecture);
    };

    // Check if summary contains "סקירה כללית" (Hebrew) or "Overview" (English) and add it to topics if it exists
    const summaryLower = lecture.summary ? lecture.summary.toLowerCase() : '';
    const hasHebrewOverview = lecture.summary && lecture.summary.includes('סקירה כללית');
    const hasEnglishOverview = summaryLower.includes('overview');
    const originalTopics = lecture.topics || [];
    
    let displayTopics = [...originalTopics];
    if (hasHebrewOverview && !originalTopics.includes('סקירה כללית')) {
      displayTopics = ['סקירה כללית', ...displayTopics];
    }
    if (hasEnglishOverview && !originalTopics.some(topic => topic.toLowerCase().includes('overview'))) {
      displayTopics = ['-Overview', ...displayTopics];
    }

    return (
      <Card className="mb-4 hover:shadow-md transition-shadow !bg-muted" dir={dir}>
        <CardContent className="p-4">
          {/* Header with Calendar Icon and Title */}
          <div className="flex items-center gap-2 mb-4" style={{ justifyContent: 'flex-start' }}>
            <Calendar className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">
              {highlightSearchTerm(`${type === 'lecture' ? 'Lecture' : 'Recitation'} | ${lecture.date ? formatDate(lecture.date) : 'No date'}`, searchQuery)}
            </h3>
          </div>

          {/* Topics Dropdown */}
          {displayTopics && displayTopics.length > 0 && (
            <div className="mb-3">
              <div 
                className="flex items-center justify-between p-3 bg-muted rounded-lg cursor-pointer hover:bg-muted/80 transition-colors border border-gray-200 dark:border-gray-600"
                onClick={() => toggleSection(lectureId, 'topics')}
              >
                <span className="text-xs font-medium text-gray-800 dark:text-gray-200">Topics</span>
                <ChevronDown 
                  className={`w-4 h-4 text-gray-600 dark:text-gray-400 transition-transform ${isTopicsExpanded ? 'rotate-180' : ''}`} 
                />
              </div>
              {isTopicsExpanded && (
                <div className="mt-2 p-3 bg-muted border border-gray-200 dark:border-gray-600 rounded-lg">
                  <div className="flex flex-wrap gap-1" style={{ justifyContent: lectureIsHebrew ? 'flex-end' : 'flex-start' }}>
                    {displayTopics.map((topic, index) => {
                      const cleanTopic = topic.replace(/^\d+\.\s*/, '').replace(/^["']|["']$/g, '');
                      return (
                        <Badge 
                          key={index} 
                          variant="outline" 
                          className="text-xs font-semibold text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 bg-muted hover:bg-primary/20 hover:border-primary cursor-pointer transition-colors"
                          style={{ textAlign }}
                          onClick={(e) => handleTopicClick(topic, index, e)}
                          title="Click to view Summary"
                        >
                          {highlightSearchTerm(cleanTopic, searchQuery)}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Summary - opens in modal */}
          {lecture.summary && (
            <div className="mb-3">
              <div 
                className="flex items-center justify-between p-3 bg-muted rounded-lg cursor-pointer hover:bg-muted/80 transition-colors border border-gray-200 dark:border-gray-600"
                onClick={() => openSummaryModal(lecture)}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-800 dark:text-gray-200">Summary</span>
                  <Badge variant="outline" className="text-[10px] text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 bg-muted">
                    {wordCount} words
                  </Badge>
                </div>
                <ChevronDown 
                  className={`w-4 h-4 text-gray-600 dark:text-gray-400 transition-transform ${summaryModalLecture?.id === lectureId ? 'rotate-180' : ''}`} 
                />
              </div>
            </div>
          )}

        </CardContent>
      </Card>
    );
  };

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      );
    }

    if (isTimeoutError) {
      return (
        <div className="text-center py-8">
          <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
          <p className="text-destructive mb-2">Unable to verify course enrollment</p>
          <p className="text-muted-foreground text-sm mb-4">The enrollment verification request timed out. Please try again.</p>
          <Button onClick={fetchLecturesData} variant="default" className="mt-2">
            Retry
          </Button>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-8">
          <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
          <p className="text-destructive">{error}</p>
          <Button onClick={fetchLecturesData} variant="outline" className="mt-2">
            Try Again
          </Button>
        </div>
      );
    }

    switch (activeTab) {
      case 'lectures':
        const filteredLectures = sortItems(filterItems(lectures));
        return (
          <div key="lectures-tab-content">
            {filteredLectures.length > 0 ? (
              filteredLectures.map((lecture, index) => (
                <LectureCard key={`lecture-${lecture.id || index}`} lecture={lecture} type="lecture" />
              ))
            ) : (
              <div className="text-center py-8">
                <Captions className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-muted-foreground">No lectures found</p>
              </div>
            )}
          </div>
        );

      case 'recitations':
        const filteredRecitations = sortItems(filterItems(recitations));
        return (
          <div key="recitations-tab-content">
            {filteredRecitations.length > 0 ? (
              filteredRecitations.map((recitation, index) => (
                <LectureCard key={`recitation-${recitation.id || index}`} lecture={recitation} type="exercise" />
              ))
            ) : (
              <div className="text-center py-8">
                <Captions className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-muted-foreground">No recitations found</p>
              </div>
            )}
          </div>
        );

      case 'materials':
        const sortedLecturesMat = sortItems(lectures);
        const sortedRecitationsMat = sortItems(recitations);
        const filteredLecturesMat = filterItems(sortedLecturesMat);
        const filteredRecitationsMat = filterItems(sortedRecitationsMat);
        
        const renderMaterialSection = (items, title, icon, type) => {
          if (items.length === 0) return null;
          
          return (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                {icon}
                <h3 className="text-lg font-semibold text-foreground dark:text-gray-100">{title}</h3>
              </div>
              <div className="space-y-2">
                {items.map((item, index) => {
                  const itemDate = item.date ? formatDate(item.date) : 'No date';
                  const typeLabel = type === 'lecture' ? 'Lecture' : 'Recitation';
                  const formattedTitle = `${typeLabel} | ${itemDate}`;
                  
                  return (
                    <div 
                      key={item.id || index}
                      className="flex items-center justify-between p-3 !bg-muted rounded-lg border border-gray-200 dark:border-gray-600 hover:shadow-sm dark:hover:shadow-md transition-shadow"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-primary" />
                          <div className="text-sm font-semibold text-foreground dark:text-gray-200">
                            {highlightSearchTerm(formattedTitle, searchQuery)}
                          </div>
                        </div>
                      </div>
                      {item.url && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => window.open(item.url, '_blank', 'noopener,noreferrer')}
                              className="flex items-center gap-1 text-muted-foreground dark:text-gray-400 hover:text-foreground dark:hover:text-gray-200"
                            >
                              <Video className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Click to Play</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        };
        
        return (
          <TooltipProvider>
            <div>
              {renderMaterialSection(filteredLecturesMat, "Lecture Videos", <Video className="h-5 w-5 text-muted-foreground" />, 'lecture')}
              {renderMaterialSection(filteredRecitationsMat, "Recitation Videos", <Users className="h-5 w-5 text-muted-foreground" />, 'recitation')}
              
              {filteredLecturesMat.length === 0 && filteredRecitationsMat.length === 0 && (
                <div className="text-center py-8">
                  <Video className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">No recordings found</p>
                </div>
              )}
            </div>
          </TooltipProvider>
        );

      default:
        return null;
    }
  };


  // Filter tabs based on available data
  const allTabs = [
    { id: 'lectures', label: 'Lectures', icon: Captions, hasData: lectures.length > 0 },
    { id: 'recitations', label: 'Recitations', icon: Captions, hasData: recitations.length > 0 },
    { id: 'materials', label: 'Recordings', icon: Video, hasData: true } // Always show materials tab
  ];
  
  const tabs = allTabs.filter(tab => tab.hasData);

  return (
    <div className="w-full h-full flex flex-col bg-muted lectures-panel relative">
      <style dangerouslySetInnerHTML={{
        __html: `
          .lectures-panel ::selection {
            background-color: #3b82f6 !important;
            color: white !important;
          }
          .lectures-panel ::-moz-selection {
            background-color: #3b82f6 !important;
            color: white !important;
          }
          .lectures-panel ::-webkit-selection {
            background-color: #3b82f6 !important;
            color: white !important;
          }
        `
      }} />
      {/* Header */}
      <div className="px-4 pt-2 pb-4 border-b border-border">
        <div className="flex items-center gap-2 mb-3">
          {onCollapse && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-2"
                    onClick={onCollapse}
                  >
                    <PanelLeftClose className="h-4 w-4 rotate-180" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Collapse</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <h2 className="text-lg font-semibold text-foreground">
            Lectures & Materials
          </h2>
        </div>
        
        {/* Search and Sort Controls */}
        <div className="mt-3 space-y-4 mb-4">
          {/* Search Bar */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 flex items-center gap-2 pl-3 rounded-md border border-primary/30 transition-all focus-within:ring-2 focus-within:ring-primary/40 focus-within:border-primary"> 
              
              <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              
              <Input
                placeholder="Search Lecture & Recitation by Keyword…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="border-0 ring-0 bg-transparent text-xs flex-1 min-w-0 -ml-2 outline-none focus:ring-0 focus:outline-none focus:border-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </div>
          </div>
        </div>

        {/* Tabs */}
        {tabs.length > 0 && (
          <div className="flex space-x-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <Button
                  key={tab.id}
                  variant={activeTab === tab.id ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 ${
                    activeTab === tab.id 
                      ? "bg-primary text-primary-foreground" 
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </Button>
              );
            })}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {renderTabContent()}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-border text-sm text-muted-foreground" dir="rtl" style={{ textAlign: 'right' }}>
        <div>{course?.label} Materials</div>
        <div>Last updated: {new Date().toLocaleDateString('he-IL')}</div>
      </div>

      {/* Summary Modal */}
      {summaryModalLecture && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50" 
            onClick={closeSummaryModal}
            aria-hidden="true"
          />
          <div 
            className="relative w-[90%] max-w-2xl max-h-[85vh] flex flex-col rounded-lg bg-background border border-gray-200 dark:border-gray-600 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Title bar with Copy and Close */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-600 bg-muted rounded-t-lg">
              <h3 className="text-sm font-semibold text-foreground truncate flex-1 mr-3">
                {summaryModalLecture.name || (summaryModalLecture.date ? `Summary - ${formatDate(summaryModalLecture.date)}` : 'Summary')}
              </h3>
              <div className="flex items-center gap-2 flex-shrink-0">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 hover:bg-primary/10 hover:text-primary transition-colors"
                        onClick={() => handleCopySummary(summaryModalLecture.summary)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Copy summary</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={closeSummaryModal}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {/* Scrollable body */}
            <div 
              ref={summaryModalRef}
              className="flex-1 overflow-y-auto p-4"
              dir="auto"
            >
              <div className="mt-2 p-3 bg-muted border border-gray-200 dark:border-gray-600 rounded-lg">
                <MarkdownRenderer
                  content={summaryModalLecture.summary}
                  dir={getTextDirection(summaryModalLecture.summary || '')}
                  textAlign={isHebrew(summaryModalLecture.summary || '') ? 'right' : 'left'}
                  searchTerm={searchQuery}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Text Selection Tooltip - Shows on right-click */}
      {showSelectionTooltip && selectedText && (
        <div
          className="selection-tooltip absolute z-50 bg-muted border border-border rounded-lg shadow-lg p-2 flex flex-col gap-2"
          style={{
            left: `${selectionPosition.x}px`,
            top: `${selectionPosition.y + 10}px`,
            minWidth: '200px'
          }}
        >
          <Button
            size="sm"
            variant="default"
            onClick={handleSendToChat}
            className="flex items-center gap-1 text-xs whitespace-nowrap w-full"
          >
            <MessageSquare className="w-3 h-3" />
            Send to Chat
          </Button>
          <div
            className="text-xs text-muted-foreground bg-muted p-2 rounded max-h-20 overflow-y-auto"
            dir={getTextDirection(selectedText)}
            style={{ textAlign: isHebrew(selectedText) ? 'right' : 'left' }}
          >
            "{selectedText.length > 50 ? selectedText.substring(0, 50) + '...' : selectedText}"
          </div>
        </div>
      )}
    </div>
  );
};

export default LecturesMaterialsPanel;